/** @jsxImportSource @emotion/react */
import React, { useState, useCallback } from 'react';
import { Alert, Button, Card, Space, Spin, Tag, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../i18n';
import commonStyles from '../../../../styles/commonStyles';
import EChart from '../../../../components/charts/EChart';
import HarvestForecastCard from './HarvestForecastCard';
import LifecycleProgressCard from './LifecycleProgressCard';
import MetricSelector from './MetricSelector';
import { useForecastChart } from '../hooks/useForecastChart';
import { styles } from '../styles';
import type { ForecastMetricKey } from '../constants';

const { Text } = Typography;

interface Props {
  forecastSeries: any;
  loadingForecastSeries: boolean;
  notPlantedDays: number;
  lifecycleTerminalInfo: { terminalStatus: string; terminalDate: string | null; terminalReason: string | null } | null;
  harvestForecast: any;
  isForecastAnnualLike: boolean;
  harvestGrossTargetKg: number | null;
  harvestMarketableTargetKg: number | null;
  plantingQuantity: number;
  showBatchQuantityMetric?: boolean;
  initialMetric?: ForecastMetricKey;
}

const ForecastCard: React.FC<Props> = ({
  forecastSeries,
  loadingForecastSeries,
  notPlantedDays,
  lifecycleTerminalInfo,
  harvestForecast,
  isForecastAnnualLike,
  harvestGrossTargetKg,
  harvestMarketableTargetKg,
  plantingQuantity,
  showBatchQuantityMetric = false,
  initialMetric = 'dimensions.height',
}) => {
  const { t } = useTranslation('plantings');
  const { saveId } = useParams<{ saveId: string }>();
  const [selectedMetric, setSelectedMetric] = useState<ForecastMetricKey>(initialMetric);
  const [showHarvestDailyBars, setShowHarvestDailyBars] = useState(false);
  const [showOptimalZone, setShowOptimalZone] = useState(true);
  const timeSeriesStatus = forecastSeries?.timeSeriesStatus || 'valid';
  const hasReplayingTimeSeries = timeSeriesStatus === 'replaying'
    || forecastSeries?.replayJobStatus === 'queued'
    || forecastSeries?.replayJobStatus === 'running';

  const forecastChartOption = useForecastChart({
    forecastSeries,
    selectedMetric,
    showOptimalZone,
    showHarvestDailyBars,
  });
  const lifecycleWithBbch = forecastSeries?.finalState?.lifecycle
    ? {
        ...forecastSeries.finalState.lifecycle,
        bbch: forecastSeries.finalState.bbch ?? forecastSeries.finalState.lifecycle.bbch ?? null,
      }
    : null;

  const handleSelectMetric = useCallback((key: ForecastMetricKey) => {
    setShowHarvestDailyBars(false);
    setSelectedMetric(key);
  }, []);

  const handleToggleHarvestBars = useCallback(() => {
    setShowHarvestDailyBars((prev) => !prev);
  }, []);

  return (
    <Card title={t('forecast.card.title')} extra={<Tag color="purple">{t('forecast.card.activeSession')}</Tag>}>
      {loadingForecastSeries ? (
        <Spin />
      ) : hasReplayingTimeSeries ? (
        <Alert
          type="info"
          showIcon
          message={t('forecast.card.replaying')}
          description={t('forecast.card.replayingDescription')}
        />
      ) : !forecastSeries?.timeSeries?.length ? (
        <Text type="secondary">{t('forecast.card.notEnoughData')}</Text>
      ) : (
        <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
          <EChart option={forecastChartOption} height={320} />

          {notPlantedDays > 0 && (
            <Text type="secondary">
              {t('forecast.card.notPlantedDays', { count: notPlantedDays })}
            </Text>
          )}

          <LifecycleProgressCard lifecycle={lifecycleWithBbch} />

          {lifecycleTerminalInfo?.terminalDate && (
            <Text type={lifecycleTerminalInfo.terminalStatus === 'dead' ? 'danger' : 'success'}>
              {lifecycleTerminalInfo.terminalStatus === 'dead'
                ? `☠ ${t('forecast.card.noLivePlantsBy', { date: formatDate(lifecycleTerminalInfo.terminalDate.slice(0, 10), 'short') })}`
                : `✓ ${t('forecast.card.lifecycleCompletedBy', { date: formatDate(lifecycleTerminalInfo.terminalDate.slice(0, 10), 'short') })}`}
              {lifecycleTerminalInfo.terminalReason ? ` · ${t('forecast.card.terminalReason', { reason: lifecycleTerminalInfo.terminalReason })}` : ''}
              {lifecycleTerminalInfo.terminalStatus ? ` · ${t('forecast.card.terminalStatus', { status: lifecycleTerminalInfo.terminalStatus })}` : ''}
            </Text>
          )}

          <HarvestForecastCard
            harvestForecast={harvestForecast}
            isForecastAnnualLike={isForecastAnnualLike}
            harvestGrossTargetKg={harvestGrossTargetKg}
            harvestMarketableTargetKg={harvestMarketableTargetKg}
            plantingQuantity={plantingQuantity}
          />

          <MetricSelector
            selectedMetric={selectedMetric}
            showHarvestDailyBars={showHarvestDailyBars}
            showOptimalZone={showOptimalZone}
            onSelectMetric={handleSelectMetric}
            onToggleHarvestBars={handleToggleHarvestBars}
            onToggleOptimalZone={setShowOptimalZone}
            plantingQuantity={plantingQuantity}
            showBatchQuantityMetric={showBatchQuantityMetric}
          />

          {saveId && (
            <Space size={8} align="center">
              <Text type="secondary" css={styles.smallText}>{t('forecast.card.storage')}</Text>
              <Link to={`/saves/${saveId}/storage`}>
                <Button size="small">{t('forecast.card.openStorage')}</Button>
              </Link>
            </Space>
          )}

          {!!forecastSeries?.stressGuide && (
            <Text type="secondary" css={styles.smallText}>
              {t('forecast.bounds.title')}
              {forecastSeries.stressGuide.temperature?.optMinC != null && forecastSeries.stressGuide.temperature?.optMaxC != null
                ? ` ${t('forecast.bounds.temperature', { min: forecastSeries.stressGuide.temperature.optMinC, max: forecastSeries.stressGuide.temperature.optMaxC })};`
                : ''}
              {forecastSeries.stressGuide.soil?.optSoilMoisturePct != null
                ? ` ${t('forecast.bounds.soilMoisture', { value: forecastSeries.stressGuide.soil.optSoilMoisturePct })}`
                : ''}
              {forecastSeries.stressGuide.soil?.minSoilMoisturePct != null && forecastSeries.stressGuide.soil?.maxSoilMoisturePct != null
                ? ` ${t('forecast.bounds.soilAcceptable', { min: forecastSeries.stressGuide.soil.minSoilMoisturePct, max: forecastSeries.stressGuide.soil.maxSoilMoisturePct })};`
                : ''}
              {forecastSeries.stressGuide.light?.defaultDliMinMol != null && forecastSeries.stressGuide.light?.defaultDliTargetMol != null
                ? ` ${t('forecast.bounds.light', { min: forecastSeries.stressGuide.light.defaultDliMinMol, max: forecastSeries.stressGuide.light.defaultDliTargetMol })};`
                : ''}
              {forecastSeries.stressGuide.light?.defaultPhotoperiodTargetHours != null
                ? ` ${t('forecast.bounds.photoperiod', { value: forecastSeries.stressGuide.light.defaultPhotoperiodTargetHours })};`
                : ''}
              {forecastSeries.stressGuide.careTargets?.watering?.triggerPct != null
                ? ` ${t('forecast.bounds.wateringTrigger', { value: forecastSeries.stressGuide.careTargets.watering.triggerPct })}`
                : ''}
              {forecastSeries.stressGuide.careTargets?.watering?.targetPct != null
                ? `, ${t('forecast.bounds.wateringTarget', { value: forecastSeries.stressGuide.careTargets.watering.targetPct })}`
                : ''}
            </Text>
          )}
        </Space>
      )}
    </Card>
  );
};

export default ForecastCard;
